import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectButton, SelectButtonModule } from 'primeng/selectbutton';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomerService } from '../../../core/services/customer.service';
import { Customer } from '../../../core/models/customer.model';
import { ReservationService } from '../../../core/services/reservation.service';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { PaginatorModule } from 'primeng/paginator';
import { MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { ContractService } from '../../../core/services/contract.service';
import { Contract } from '../../../core/models/contract.model';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AuthService } from '../../../core/services/auth';
import { PermissionService } from '../../../core/services/permission.service';
import { FileIconService, FileIconInfo } from '../../../core/services/file-icon.service';
import { SubscriberService } from '../../../core/services/subscriber.service';
import { SubscriptionContextService } from '../../../core/services/subscription-context.service';
import { firstValueFrom } from 'rxjs';

interface DecisionMaker {
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
}

@Component({
  selector: 'app-view-customer',
  imports: [
    CommonModule,
    FormsModule,
    SelectButton,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    SelectButtonModule,
    PaginatorModule,
    MenuModule,
    ConfirmDialogModule,
    ToastModule,
    CardModule,
    BadgeModule,
    AvatarModule,
    TooltipModule,
    DrawerModule,
    DialogModule,
    TextareaModule,
    TagModule,
    IconFieldModule,
    RouterLink
  ],
  templateUrl: './view-customer.html',
  styleUrl: './view-customer.scss',
  providers: [MessageService, ConfirmationService]
})
export class ViewCustomer implements OnInit {
  @ViewChild('menu') menu: any;

  stateOptions: any[] = [
    { label: 'Reservations', value: 'reservations' },
    { label: 'Customer Information', value: 'customer-info' },
    { label: 'Contracts', value: 'contracts' }
  ];
  value: string = 'reservations';

  // Customer data
  customer: Customer | null = null;
  decisionMakers: DecisionMaker[] = [];
  isLoading: boolean = true;
  error: string | null = null;
  customerNotesSafe: SafeHtml | null = null;
  
  // Reservation data
  reservations: any[] = [];
  isLoadingReservations: boolean = false;
  isLoadingContracts: boolean = false;
  contracts: Contract[] = [];
  totalContracts: number = 0;
  // Sorting
  sortField: string = 'createdAt';
  sortOrder: number = -1; // -1 for descending, 1 for ascending
  
  // Action menu
  actionMenuItems: any[] = [];
  
  // Drawer for contract review
  isDrawerVisible = false;
  selectedContractForReview: Contract | null = null;
  contractReviewLoading = false;
  customerDocuments: string[] = [];

  // Rejection reason dialog
  showRejectionDialog = false;
  rejectionReason = '';
  contractToReject: Contract | null = null;

  // Termination reason dialog
  showTerminationDialog = false;
  terminationReason = '';
  contractToTerminate: string | null = null;

  // Current user context for role hierarchy checks
  currentUser: any = null;
  
  // Track which terminated contracts can create new contracts
  canCreateNewContract: { [contractId: string]: boolean } = {};
  checkingContractCreation: { [contractId: string]: boolean } = {};

  // Subscriber document tracking
  selectedSubscriberId: string | null = null;
  subscriberTermsConditionsUrl: string = '';
  subscriberPrivacyPolicyUrl: string = '';
  subscriberTermsConditionsFileName: string = '';
  subscriberPrivacyPolicyFileName: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private reservationService: ReservationService,
    private contractService: ContractService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private permissionService: PermissionService,
    private fileIconService: FileIconService,
    private subscriberService: SubscriberService,
    private subscriptionContextService: SubscriptionContextService
  ) {}

  ngOnInit() {
    // Load current user context first
    this.currentUser = this.authService.getLoggedInUser();
    console.log('ViewCustomer - Current User:', this.currentUser);
    
    // Get selected subscriber ID from subscription context
    this.selectedSubscriberId = this.subscriptionContextService.getSelectedSubscriberId();
    
    this.loadCustomerData();
    this.checkTabQueryParam();
    this.loadSubscriberDocuments();
  }

  private checkTabQueryParam() {
    this.route.queryParams.subscribe(params => {
      if (params['tab']) {
        const tabValue = params['tab'];
        if (['reservations', 'customer-info', 'contracts'].includes(tabValue)) {
          this.value = tabValue;
          // Load data for the selected tab
          this.onTabChange();
        }
      }
    });
  }

  // Load reservations when tab changes
  onTabChange() {
    if (this.value === 'reservations') {
      this.loadReservations();
    } else if (this.value === 'contracts') {
      this.loadContracts();
    }
  }

  // Check if current user is FSR
  isFSR(): boolean {
    const roleIdentification = this.currentUser?.roleId?.identification || this.currentUser?.role?.identification;
    return roleIdentification === 'FSR';
  }

  // Check if current user is SA or A (should not have access to certain actions)
  isSAOrA(): boolean {
    const roleIdentification = this.currentUser?.roleId?.identification || this.currentUser?.role?.identification;
    return roleIdentification === 'SA' || roleIdentification === 'A';
  }

  loadCustomerData() {
    this.route.params.subscribe(params => {
      const customerId = params['id'];
      if (customerId) {
        this.fetchCustomerDetails(customerId);
      } else {
        this.error = 'Customer ID not found';
        this.isLoading = false;
      }
    });
  }

  private fetchCustomerDetails(customerId: string) {
    this.isLoading = true;
    this.error = null;

    this.customerService.getCustomerById(customerId).subscribe({
      next: (response: any) => {
        if (response && response.customer) {
          this.customer = response.customer;
          this.decisionMakers = response.customer.decisionMaker || [];
          this.customerNotesSafe = this.sanitizeHtml(response.customer.notes || '');
          this.customerDocuments = this.getCustomerDocuments(response.customer);
          this.isLoading = false;
          
          // Load data for the selected tab
          if (this.value === 'reservations') {
            this.loadReservations();
          } else if (this.value === 'contracts') {
            this.loadContracts();
          }
        } else {
          this.error = 'Customer not found';
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error fetching customer:', error);
        this.error = 'Failed to load customer details';
        this.isLoading = false;
      }
    });
  }

  private loadReservations() {
    if (!this.customer?._id) return;
    
    this.isLoadingReservations = true;
    this.reservationService.getReservationsByCustomerId(this.customer._id).subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.reservations = response.data;
          // Apply sorting after loading data
          this.sortReservations();
          // Load contract status for each reservation (loader will be hidden when complete)
          this.loadContractStatusForReservations();
        } else {
          this.reservations = [];
          this.isLoadingReservations = false;
        }
      },
      error: (error) => {
        console.error('Error fetching reservations:', error);
        this.reservations = [];
        this.isLoadingReservations = false;
      }
    });
  }

  private loadContracts() {
    if (!this.customer?._id) return;
    this.isLoadingContracts = true;
    this.contractService.getContractsByCustomerId(this.customer._id).subscribe({
      next: (response: any) => {
        console.log('Contracts API Response:', response);
        this.contracts = response.data || [];
        this.totalContracts = this.contracts.length;
        console.log('Contracts loaded:', this.contracts.length);
        this.isLoadingContracts = false;
        // Apply sorting after loading data
        this.sortContracts();
      },
      error: (error) => {
        console.error('Error fetching contracts:', error);
        this.contracts = [];
        this.totalContracts = 0;
        this.isLoadingContracts = false;
      }
    });
  }

  onUpdateClick() {
    if (this.customer) {
      this.router.navigate(['/admin/add-edit-customer'], { 
        queryParams: { 
          mode: 'edit', 
          id: this.customer._id,
          returnUrl: `/admin/view-customer/${this.customer._id}`
        } 
      });
    }
  }

  getBusinessName(): string {
    return this.customer?.businessName || 'N/A';
  }

  getBusinessId(): string {
    return this.customer?.businessId || 'N/A';
  }

  getNumberOfEmployees(): string {
    return this.customer?.numberOfEmployees || 'N/A';
  }

  getZipCode(): string {
    return this.customer?.zipCode || 'N/A';
  }

  getIndustry(): string {
    return this.customer?.industry || 'N/A';
  }

  getInterestLevel(): string {
    return this.customer?.interestLevel || 'N/A';
  }

  getAcceptedPromotionalGifts(): string {
    return this.customer?.acceptedPromotionalGifts ? 'Yes' : 'No';
  }

  getCustomerOrigin(): string {
    return this.customer?.customerOrigin || 'N/A';
  }

  getCustomerNotes(): SafeHtml | string {
    return this.customerNotesSafe || 'N/A';
  }

  // Utility methods for reservations
  getInitials(firstName: string, lastName: string): string {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  }

  truncateEmail(email: string, maxLength: number = 18): string {
    if (!email) return '';
    if (email.length <= maxLength) return email;
    return email.substring(0, maxLength) + '...';
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(date: string | Date): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  formatExpiryDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays === 1) return 'Expires tomorrow';
    return `Expires in ${diffDays} days`;
  }

  getStatusSeverity(status: string): string {
    switch (status?.toLowerCase()) {
      case 'ongoing': return 'success';
      case 'completed': return 'info';
      case 'expired': return 'danger';
      case 'upcoming': return 'warning';
      case 'pending': return 'warn';
      case 'interested': return 'success';
      case 'not_interested': return 'danger';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  }

  viewReservationDetails(reservation: any) {
    this.router.navigate(['/admin/view-reservation', reservation._id]);
  }

  scheduleVisit(reservation: any) {
    this.router.navigate(['/admin/shedule-interaction', reservation._id]);
  }

  newReservation() {
    if (!this.customer) {
      console.error('No customer data available');
      return;
    }

    // Collect all unique products from customer's reservations
    const allProducts = new Set<string>();
    this.reservations.forEach(reservation => {
      if (reservation.products && Array.isArray(reservation.products)) {
        reservation.products.forEach((product: any) => {
          if (product._id) {
            allProducts.add(product._id);
          }
        });
      }
    });

    const productIds = Array.from(allProducts);

    // Navigate to add-reservation with pre-filled data
    this.router.navigate(['/admin/customer-lookup'], {
      queryParams: {
        customerId: this.customer._id,
        productIds: productIds.join(','),
        businessName: this.customer.businessName
      }
    });
  }

  isScheduleVisitStatus(status: string): boolean {
    if (!status) return false;
    const normalizedStatus = status.toLowerCase().replace(/[_\s]/g, '');
    console.log(`Checking schedule visit status: "${status}" -> "${normalizedStatus}"`);
    return ['pending', 'interested', 'ongoing', 'upcoming'].includes(normalizedStatus);
  }

  isViewHistoryStatus(status: string): boolean {
    if (!status) return false;
    const normalizedStatus = status.toLowerCase().replace(/[_\s]/g, '');
    return ['expired', 'converted', 'notinterested', 'completed', 'cancelled'].includes(normalizedStatus);
  }

  // Check if a reservation has a contract using API
  hasContract(reservation: any): boolean {
    // This will be updated by the API call
    return reservation._hasContract || false;
  }

  // Check if reservation is converted and doesn't have a contract
  shouldShowCreateContractButton(reservation: any): boolean {
    if (!reservation) return false;
    const normalizedStatus = reservation.status?.toLowerCase().replace(/[_\s]/g, '');
    return normalizedStatus === 'converted' && !this.hasContract(reservation);
  }

  // Load contract status for all reservations
  private loadContractStatusForReservations(): void {
    if (!this.reservations || this.reservations.length === 0) {
      this.isLoadingReservations = false;
      return;
    }

    let completedChecks = 0;
    const totalChecks = this.reservations.length;

    this.reservations.forEach(reservation => {
      this.contractService.checkReservationHasContract(reservation._id).subscribe({
        next: (response) => {
          if (response.success) {
            reservation._hasContract = response.hasContract;
          }
          completedChecks++;
          
          // Hide loader when all checks are complete
          if (completedChecks === totalChecks) {
            this.isLoadingReservations = false;
          }
        },
        error: (error) => {
          console.error('Error checking contract status for reservation:', reservation._id, error);
          reservation._hasContract = false;
          completedChecks++;
          
          // Hide loader when all checks are complete (even if some failed)
          if (completedChecks === totalChecks) {
            this.isLoadingReservations = false;
          }
        }
      });
    });
  }

  // Navigate to create contract page
  createContract(reservation: any): void {
    if (reservation?._id) {
      this.router.navigate(['/admin/add-edit-contract', reservation._id]);
    }
  }


  logInteraction(reservation: any) {
    this.router.navigate(['/admin/log-interaction/create', reservation._id]);
  }

  showActionMenu(event: any, reservation: any) {
    event.stopPropagation();
    
    this.actionMenuItems = [
      // {
      //   label: 'Log Interaction',
      //   icon: 'pi pi-calendar-plus',
      //   command: () => this.router.navigate(['/admin/log-interaction/create', reservation._id])
      // },
      // {
      //   label: 'View Interactions',
      //   icon: 'pi pi-eye',
      //   command: () => this.router.navigate(['/admin/view-reservation', reservation._id])
      // }
    ];

    // Add Create Contract for converted reservations without contracts
    if (this.shouldShowCreateContractButton(reservation)) {
      // this.actionMenuItems.push({
      //   label: 'Create Contract',
      //   icon: 'pi pi-file-plus',
      //   command: () => this.createContract(reservation)
      // });
    }
    
    // Show the menu at the event position
    if (this.menu) {
      this.menu.show(event);
    }
  }

  getProductNames(products: any[]): string {
    if (!products || products.length === 0) return 'No products';
    return products.map(p => p.name).join(', ');
  }

  // Sorting methods
  onSort(event: any) {
    this.sortField = event.field;
    this.sortOrder = event.order;
    this.sortReservations();
  }

  sortReservations() {
    if (!this.sortField) return;

    this.reservations.sort((a, b) => {
      let aValue = a[this.sortField];
      let bValue = b[this.sortField];

      // Handle date fields
      if (this.sortField === 'createdAt' || this.sortField === 'expiryDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle string fields
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return this.sortOrder * -1;
      }
      if (aValue > bValue) {
        return this.sortOrder * 1;
      }
      return 0;
    });
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) {
      return 'pi pi-sort';
    }
    return this.sortOrder === 1 ? 'pi pi-sort-up' : 'pi pi-sort-down';
  }

  // Contract-related methods
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  getStatusBadgeSeverity(status: string, endDate?: string | Date): string {
    if (status?.toUpperCase() === 'ACTIVE' && this.isExpiringSoon(endDate)) {
      return 'warn';
    }
    
    switch (status?.toUpperCase()) {
      case 'DRAFT':
        return 'info';
      case 'ACTIVE':
        return 'success';
      case 'EXPIRED':
        return 'danger';
      case 'TERMINATED':
        return 'danger';
      case 'REJECTED':
        return 'secondary';
      default:
        return 'info';
    }
  }

  getStatusLabel(status: string, endDate?: string | Date): string {
    if (status?.toUpperCase() === 'ACTIVE' && this.isExpiringSoon(endDate)) {
      return 'Expiring Soon';
    }
    
    switch (status?.toUpperCase()) {
      case 'DRAFT':
        return 'Draft';
      case 'ACTIVE':
        return 'Active';
      case 'EXPIRED':
        return 'Expired';
      case 'TERMINATED':
        return 'Terminated';
      case 'REJECTED':
        return 'Rejected';
      default:
        return status || 'Unknown';
    }
  }

  isExpiringSoon(endDate?: string | Date): boolean {
    if (!endDate) return false;
    
    const endDateObj = typeof endDate === 'string' ? new Date(endDate) : endDate;
    const today = new Date();
    const oneWeekFromNow = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));
    
    return endDateObj <= oneWeekFromNow && endDateObj >= today;
  }

  toggleSort(): void {
    this.sortOrder = this.sortOrder === -1 ? 1 : -1;
    this.sortContracts();
  }

  sortContracts(): void {
    if (!this.sortField) return;

    this.contracts.sort((a, b) => {
      let aValue = (a as any)[this.sortField];
      let bValue = (b as any)[this.sortField];

      // Handle date fields
      if (this.sortField === 'createdAt' || this.sortField === 'endDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle string fields
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return this.sortOrder * -1;
      }
      if (aValue > bValue) {
        return this.sortOrder * 1;
      }
      return 0;
    });
  }

  reviewContract(contract: Contract): void {
    this.selectedContractForReview = contract;
    this.isDrawerVisible = true;
  }

  showContractActionMenu(event: Event, contract: Contract): void {
    event.stopPropagation();
    
    this.actionMenuItems = [
      {
        label: 'View Details',
        icon: 'pi pi-eye',
        command: () => this.viewContract(contract)
      }
    ];

    // Only add Update Contract option if not terminated
    if (contract.status?.toUpperCase() !== 'TERMINATED') {
      this.actionMenuItems.push({
        label: 'Update Contract',
        icon: 'pi pi-pencil',
        command: () => this.updateContract(contract)
      });
    }

    // Add terminate option for ACTIVE contracts
    if ((contract.status?.toUpperCase() === 'ACTIVE' || 
        (contract.status?.toUpperCase() === 'ACTIVE' && this.isExpiringSoon(contract.endDate))) &&
        this.permissionService.hasPermission('contract.terminate')) {
      this.actionMenuItems.push({
        label: 'Terminate Contract',
        icon: 'pi pi-times-circle',
        command: () => this.terminateContract(contract._id)
      });
    }
    
    // Show the menu at the event position
    if (this.menu) {
      this.menu.show(event);
    }
  }

  viewContract(contract: Contract): void {
    this.selectedContractForReview = contract;
    this.isDrawerVisible = true;
    
    // Check if we can create a new contract for terminated contracts
    if (contract.status?.toUpperCase() === 'TERMINATED') {
      this.checkIfCanCreateNewContract(contract);
    }
  }

  updateContract(contract: Contract): void {
    if (contract.status?.toUpperCase() === 'TERMINATED') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Update',
        detail: 'Terminated contracts cannot be updated. Please create a new contract instead.'
      });
      return;
    }
    this.router.navigate(['/admin/add-edit-contract/update', contract._id]);
  }

  terminateContract(contractId: string): void {
    this.contractToTerminate = contractId;
    this.terminationReason = '';
    this.showTerminationDialog = true;
  }

  confirmTermination(): void {
    if (!this.contractToTerminate) return;
    
    this.updateContractStatus(this.contractToTerminate, 'TERMINATED', undefined, this.terminationReason);
    this.showTerminationDialog = false;
    this.contractToTerminate = null;
    this.terminationReason = '';
  }

  cancelTermination(): void {
    this.showTerminationDialog = false;
    this.contractToTerminate = null;
    this.terminationReason = '';
  }

  async checkIfCanCreateNewContract(contract: Contract): Promise<void> {
    const contractId = contract._id;
    
    // If already checking or already determined, skip
    if (this.checkingContractCreation[contractId] || this.canCreateNewContract.hasOwnProperty(contractId)) {
      return;
    }
    
    this.checkingContractCreation[contractId] = true;
    
    try {
      const reservationId = typeof contract.reservationId === 'string' 
        ? contract.reservationId 
        : (contract.reservationId as any)?._id || contract.reservationId;
      
      if (!reservationId) {
        this.canCreateNewContract[contractId] = false;
        return;
      }
      
      // Load reservation to get products
      const reservationResponse: any = await firstValueFrom(this.reservationService.getReservationById(reservationId));
      const reservation = reservationResponse?.data || reservationResponse;
      
      if (!reservation || !reservation.products || reservation.products.length === 0) {
        this.canCreateNewContract[contractId] = false;
        return;
      }
      
      const reservationProductIds = reservation.products.map((p: any) => 
        typeof p === 'string' ? p : (p._id || p)
      ).map((id: any) => id.toString());
      
      // Load all contracts for the customer
      const customerId = typeof contract.customerId === 'string' 
        ? contract.customerId 
        : (contract.customerId as any)?._id || contract.customerId;
      
      const contractsResponse: any = await firstValueFrom(this.contractService.getContractsByCustomerId(customerId));
      const contracts = contractsResponse?.data || contractsResponse || [];
      
      // Filter out terminated contracts, expired contracts, and the current contract
      const activeContracts = contracts.filter((c: Contract) => 
        c._id !== contractId && 
        c.status?.toUpperCase() !== 'TERMINATED' &&
        c.status?.toUpperCase() !== 'EXPIRED'
      );
      
      // Get all product IDs that already have contracts
      const contractedProductIds = new Set<string>();
      activeContracts.forEach((c: Contract) => {
        if (c.products) {
          c.products.forEach((productId: string) => {
            const pid = typeof productId === 'string' ? productId : (productId as any)?._id || productId;
            contractedProductIds.add(pid.toString());
          });
        }
      });
      
      // Check if there are any products from reservation that don't have contracts
      const availableProducts = reservationProductIds.filter((productId: string) => {
        return !contractedProductIds.has(productId);
      });
      
      this.canCreateNewContract[contractId] = availableProducts.length > 0;
    } catch (error) {
      console.error('Error checking if can create new contract:', error);
      this.canCreateNewContract[contractId] = false;
    } finally {
      this.checkingContractCreation[contractId] = false;
    }
  }

  addNewContractForCustomer(contract: Contract): void {
    const customerId = typeof contract.customerId === 'string' 
      ? contract.customerId 
      : (contract.customerId as any)?._id || contract.customerId;
    
    const reservationId = typeof contract.reservationId === 'string' 
      ? contract.reservationId 
      : (contract.reservationId as any)?._id || contract.reservationId;
    
    if (customerId) {
      const queryParams: any = { customerId: customerId };
      if (reservationId) {
        queryParams.reservationId = reservationId;
      }
      
      this.router.navigate(['/admin/add-edit-contract'], { 
        queryParams: queryParams
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to determine customer ID'
      });
    }
  }

  closeContractReview(): void {
    this.isDrawerVisible = false;
    this.selectedContractForReview = null;
  }

  getCustomerName(contract: Contract): string {
    return (contract.customerId as any)?.businessName || 'Customer Name';
  }

  getProductName(product: any): string {
    return product?.name || product || 'Unknown Product';
  }

  hasProductValues(contract: Contract): boolean {
    if (!contract.productValues) return false;
    if (contract.productValues instanceof Map) {
      return contract.productValues.size > 0;
    }
    return Object.keys(contract.productValues).length > 0;
  }

  getProductValue(product: any, productValues: any): string {
    if (!productValues) return '';
    
    // Handle both Map and object formats
    let productId: string;
    if (typeof product === 'string') {
      productId = product;
    } else if (product?._id) {
      productId = product._id;
    } else {
      return '';
    }
    
    let value: number | undefined;
    if (productValues instanceof Map) {
      value = productValues.get(productId);
    } else {
      value = productValues[productId];
    }
    
    if (value === undefined || value === null) return '';
    return this.formatCurrency(value);
  }

  getFSRName(contract: Contract): string {
    return `${(contract.fsrId as any)?.firstName || ''} ${(contract.fsrId as any)?.lastName || ''}`.trim() || 'FSR Name';
  }

  getApproverName(contract: Contract): string {
    if (!contract.approverId) return 'Not Approved';
    const approver = contract.approverId as any;
    return `${approver.firstName || ''} ${approver.lastName || ''}`.trim() || 'Not Approved';
  }

  approveContract(contractId: string): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to approve this contract?',
      header: 'Approve Contract',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.updateContractStatus(contractId, 'ACTIVE');
      }
    });
  }

  denyContract(contractId: string): void {
    const contract = this.contracts.find(c => c._id === contractId);
    if (!contract) return;
    
    this.contractToReject = contract;
    this.rejectionReason = '';
    this.showRejectionDialog = true;
  }

  confirmRejection(): void {
    if (!this.contractToReject || !this.rejectionReason || this.rejectionReason.trim() === '') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please provide a rejection reason'
      });
      return;
    }

    this.updateContractStatus(this.contractToReject._id, 'REJECTED', this.rejectionReason);
    this.showRejectionDialog = false;
    this.contractToReject = null;
    this.rejectionReason = '';
  }

  cancelRejection(): void {
    this.showRejectionDialog = false;
    this.contractToReject = null;
    this.rejectionReason = '';
  }

  private updateContractStatus(contractId: string, status: string, rejectionReason?: string, terminationReason?: string): void {
    this.contractReviewLoading = true;
    
    const updateData: any = { status };
    if (rejectionReason) {
      updateData.rejectionReason = rejectionReason;
    }
    if (terminationReason) {
      updateData.terminationReason = terminationReason;
    }
    
    this.contractService.updateContract(contractId, updateData).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Contract ${status.toLowerCase()} successfully`
        });
        
        // Close drawer and refresh contracts
        this.closeContractReview();
        this.loadContracts();
      },
      error: (error) => {
        console.error('Error updating contract status:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Failed to update contract status'
        });
        this.contractReviewLoading = false;
      }
    });
  }

  // Method to safely render HTML content with proper link and list handling
  sanitizeHtml(content: string): SafeHtml {
    if (!content) return '';
    
    // Process the content to ensure links and lists work correctly
    let processedContent = content;
    
    // Ensure links have proper target="_blank" and rel="noopener noreferrer"
    processedContent = processedContent.replace(
      /<a\s+href=/gi,
      '<a target="_blank" rel="noopener noreferrer" href='
    );
    
    // Add CSS classes to links for styling
    processedContent = processedContent.replace(
      /<a\s+/gi,
      '<a class="content-link" '
    );
    
    // Add CSS classes to lists for proper styling
    processedContent = processedContent.replace(
      /<ul\s+/gi,
      '<ul class="content-list content-ul" '
    );
    
    processedContent = processedContent.replace(
      /<ol\s+/gi,
      '<ol class="content-list content-ol" '
    );
    
    // Handle lists without attributes
    processedContent = processedContent.replace(
      /<ul>/gi,
      '<ul class="content-list content-ul">'
    );
    
    processedContent = processedContent.replace(
      /<ol>/gi,
      '<ol class="content-list content-ol">'
    );
    
    // Add CSS classes to list items
    processedContent = processedContent.replace(
      /<li\s+/gi,
      '<li class="content-list-item" '
    );
    
    // Handle list items without attributes
    processedContent = processedContent.replace(
      /<li>/gi,
      '<li class="content-list-item">'
    );
    
    return this.sanitizer.bypassSecurityTrustHtml(processedContent);
  }

  // Method to open document in new tab
  openDocument(documentUrl: string): void {
    if (!documentUrl) return;
    
    // Simple: if it's not a full URL, add the S3 base URL
    const fullUrl = documentUrl.startsWith('http') 
      ? documentUrl 
      : `https://purpledev.s3.us-east-2.amazonaws.com/office-global/${documentUrl}`;
    
    window.open(fullUrl, '_blank');
  }

  // Helper method to get attachment display name
  // Get file icon information
  getFileIcon(fileUrl: string): FileIconInfo {
    return this.fileIconService.getFileIcon(fileUrl);
  }

  getDocumentDisplayName(documentUrl: string, index: number): string {
    if (!documentUrl) return '';
    
    // First, check if this is a subscriber document (Terms and Conditions or Privacy Policy)
    const resolvedName = this.resolveSubscriberDocumentName(documentUrl);
    if (resolvedName) {
      return resolvedName;
    }
    
    // Try to extract filename from URL
    try {
      const urlParts = documentUrl.split('/');
      const filename = urlParts[urlParts.length - 1];
      // Remove query parameters if any
      const cleanFilename = filename.split('?')[0];
      if (cleanFilename && cleanFilename.length > 0) {
        return cleanFilename;
      }
    } catch (e) {
      // Fallback to generic name
    }
    
    return `Attachment ${index + 1}`;
  }

  // Helper method to get documents as an array (handles both string and array formats)
  getCustomerDocuments(customer: Customer | null): string[] {
    if (!customer || !customer.documents) {
      return [];
    }
    
    // If documents is already an array, return it
    if (Array.isArray(customer.documents)) {
      return customer.documents.filter(doc => doc && typeof doc === 'string' && doc.trim() !== '');
    }
    
    // Ensure documents is a string before processing
    if (typeof customer.documents !== 'string') {
      return [];
    }
    
    // If documents is a string, check if it's a comma-separated list or JSON array
    const docs = customer.documents.trim();
    
    // Try to parse as JSON array first
    try {
      const parsed = JSON.parse(docs);
      if (Array.isArray(parsed)) {
        return parsed.filter(doc => doc && doc.trim() !== '');
      }
    } catch (e) {
      // Not JSON, continue
    }
    
    // Check if it's a comma-separated string
    if (docs.includes(',')) {
      return docs.split(',').map(doc => doc.trim()).filter(doc => doc !== '');
    }
    
    // Single document string
    return [docs];
  }

  private loadSubscriberDocuments(): void {
    const subscriberId =
      this.selectedSubscriberId ||
      this.currentUser?.subscriberId?._id ||
      this.currentUser?.subscriberId;

    if (!subscriberId) {
      this.subscriberTermsConditionsUrl = '';
      this.subscriberPrivacyPolicyUrl = '';
      this.subscriberTermsConditionsFileName = '';
      this.subscriberPrivacyPolicyFileName = '';
      return;
    }

    this.subscriberService
      .findSubscriber(subscriberId)
      .then((subscriber: any) => {
        let actualSubscriber = subscriber;
        if (subscriber && subscriber.subscriber) {
          actualSubscriber = subscriber.subscriber;
        }

        const termsUrl = this.normalizeDocumentIdentifier(actualSubscriber?.termsConditions || '');
        const privacyUrl = this.normalizeDocumentIdentifier(actualSubscriber?.privacyPolicy || '');

        this.subscriberTermsConditionsUrl = termsUrl;
        this.subscriberPrivacyPolicyUrl = privacyUrl;
        this.subscriberTermsConditionsFileName = this.extractFileName(termsUrl);
        this.subscriberPrivacyPolicyFileName = this.extractFileName(privacyUrl);
      })
      .catch((error: any) => {
        console.error('Failed to load subscriber documents for contracts:', error);
        this.subscriberTermsConditionsUrl = '';
        this.subscriberPrivacyPolicyUrl = '';
        this.subscriberTermsConditionsFileName = '';
        this.subscriberPrivacyPolicyFileName = '';
      });
  }

  private resolveSubscriberDocumentName(documentUrl: string): string | null {
    const normalizedUrl = this.normalizeDocumentIdentifier(documentUrl);
    const fileName = this.extractFileName(normalizedUrl);

    if (this.matchesSubscriberDocument(normalizedUrl, fileName, this.subscriberTermsConditionsUrl, this.subscriberTermsConditionsFileName)) {
      return 'Terms and Conditions';
    }

    if (this.matchesSubscriberDocument(normalizedUrl, fileName, this.subscriberPrivacyPolicyUrl, this.subscriberPrivacyPolicyFileName)) {
      return 'Privacy Policy';
    }

    return null;
  }

  private matchesSubscriberDocument(
    documentUrl: string,
    documentFileName: string,
    subscriberUrl: string,
    subscriberFileName: string
  ): boolean {
    if (!subscriberUrl && !subscriberFileName) {
      return false;
    }

    const normalizedSubscriberUrl = this.normalizeDocumentIdentifier(subscriberUrl);
    const normalizedSubscriberFileName = this.extractFileName(normalizedSubscriberUrl);
    const compareFileName = subscriberFileName || normalizedSubscriberFileName;

    return (
      (!!normalizedSubscriberUrl && normalizedSubscriberUrl === documentUrl) ||
      (!!compareFileName && compareFileName === documentFileName)
    );
  }

  private normalizeDocumentIdentifier(url: string): string {
    if (!url) {
      return '';
    }
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      return '';
    }
    const [pathWithoutQuery] = trimmedUrl.split('?');
    const [pathWithoutHash] = pathWithoutQuery.split('#');
    return pathWithoutHash;
  }

  private extractFileName(url: string): string {
    if (!url) {
      return '';
    }

    const cleanedUrl = this.normalizeDocumentIdentifier(url);
    const segments = cleanedUrl.split('/');
    const lastSegment = segments.pop() || '';

    return lastSegment.trim();
  }
}